import { useMemo, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Badge, { type BadgeTone } from '@/components/base/Badge';
import Button from '@/components/base/Button';
import { creators } from '@/mocks/creators';
import { formatCompact, formatDate, formatMoney } from '@/utils/format';
import type { ApplicantProfile, ApplicantStage, CampaignApplicant } from '@/types/portal';
import type { ApplicantStageMap } from '@/utils/applicantStore';

const STAGE_META: Record<
  ApplicantStage,
  { label: string; icon: string; tone: BadgeTone }
> = {
  New: { label: 'New applicant', icon: 'ri-sparkling-line', tone: 'neutral' },
  Reviewing: { label: 'Under review', icon: 'ri-search-eye-line', tone: 'primary' },
  Hired: { label: 'Hired', icon: 'ri-check-double-line', tone: 'accent' },
  Declined: { label: 'Declined', icon: 'ri-close-line', tone: 'outline' },
};

const STAGE_ORDER: ApplicantStage[] = ['New', 'Reviewing', 'Hired', 'Declined'];

/** The four beats that take an interested creator from application to engagement. */
const FLOW_STEPS = [
  {
    icon: 'ri-heart-3-line',
    title: 'Creators express interest',
    body: 'Matching creators raise their hand from their dashboard while the campaign is open.',
  },
  {
    icon: 'ri-search-eye-line',
    title: 'You review the fit',
    body: 'Compare reach, category and their note against the brief.',
  },
  {
    icon: 'ri-user-add-line',
    title: 'Invite to the campaign',
    body: 'Approved applicants join the roster as invited creators.',
  },
  {
    icon: 'ri-file-paper-2-line',
    title: 'Contract and deliver',
    body: 'They continue through the normal contract and payment flow.',
  },
];

interface ApplicantsPanelProps {
  applicants: CampaignApplicant[];
  /** Local review overrides keyed by applicant id. */
  stages: ApplicantStageMap;
  onSetStage: (applicantId: string, stage: ApplicantStage) => void;
  onHire: (applicant: CampaignApplicant) => void;
  onViewContracts: () => void;
}

export default function ApplicantsPanel({
  applicants,
  stages,
  onSetStage,
  onHire,
  onViewContracts,
}: ApplicantsPanelProps) {
  const [filter, setFilter] = useState<'all' | ApplicantStage>('all');

  const resolved = useMemo(
    () =>
      applicants.map((applicant) => ({
        applicant,
        stage: stages[applicant.id] ?? applicant.stage,
      })),
    [applicants, stages],
  );

  const counts = useMemo(() => {
    const base: Record<ApplicantStage, number> = {
      New: 0,
      Reviewing: 0,
      Hired: 0,
      Declined: 0,
    };
    resolved.forEach((entry) => {
      base[entry.stage] += 1;
    });
    return base;
  }, [resolved]);

  const visible = filter === 'all' ? resolved : resolved.filter((entry) => entry.stage === filter);

  const chipClass = (active: boolean) =>
    `inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-label font-medium whitespace-nowrap cursor-pointer transition-colors ${
      active
        ? 'border-secondary-400 bg-secondary-100 text-secondary-900'
        : 'border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
    }`;

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex flex-col gap-3 border-b border-background-200 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
            <i className="ri-inbox-archive-line" />
          </span>
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950">
              Applications from creators
            </h3>
            <p className="mt-0.5 text-xs text-foreground-500">
              Creators who found this open campaign and expressed interest.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setFilter('all')} className={chipClass(filter === 'all')}>
            All {resolved.length}
          </button>
          {STAGE_ORDER.map((stage) => (
            <button
              key={stage}
              type="button"
              onClick={() => setFilter(stage)}
              className={chipClass(filter === stage)}
            >
              {STAGE_META[stage].label} {counts[stage]}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 border-b border-background-200 bg-background-100/60 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
        {FLOW_STEPS.map((step, index) => (
          <div key={step.title} className="flex items-start gap-3">
            <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-800 text-sm">
              <i className={step.icon} />
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary-500 text-[9px] font-label font-semibold text-background-50">
                {index + 1}
              </span>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-label font-semibold text-foreground-900">{step.title}</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-foreground-500">{step.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="divide-y divide-background-100">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1 px-5 py-12 text-center">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-lg">
              <i className="ri-inbox-line" />
            </span>
            <p className="text-sm text-foreground-500">No applications in this stage yet.</p>
          </div>
        ) : (
          visible.map(({ applicant, stage }) => {
            // API applicants carry their profile; mock ones are joined from the creators mock.
            const creator: ApplicantProfile | undefined =
              applicant.creator ?? creators.find((entry) => entry.id === applicant.creatorId);
            if (!creator) return null;
            const meta = STAGE_META[stage];
            return (
              <div key={applicant.id} className="px-5 py-4">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Avatar src={creator.avatar} alt={creator.name} size="md" />

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-heading text-sm font-semibold text-foreground-950">
                            {creator.name}
                          </h4>
                          <Badge tone={meta.tone} icon={meta.icon}>
                            {meta.label}
                          </Badge>
                        </div>
                        <p className="mt-0.5 text-xs text-foreground-500">
                          {[creator.handle, creator.city].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <span className="inline-flex flex-col items-center rounded-md bg-accent-100 px-2.5 py-1 text-accent-800">
                        <span className="font-heading text-sm font-bold leading-none">
                          {creator.fitScore ?? '—'}
                        </span>
                        <span className="text-[9px] font-label uppercase tracking-wide">fit</span>
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {creator.categories.map((category) => (
                        <Badge key={category} tone="secondary">
                          {category}
                        </Badge>
                      ))}
                      <span className="inline-flex items-center gap-1 rounded-full border border-background-200 px-2 py-0.5 text-[11px] text-foreground-600">
                        <i className="ri-user-line text-[12px]" />
                        {formatCompact(creator.followers)} followers · {creator.engagementRate}% eng.
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-background-200 px-2 py-0.5 text-[11px] text-foreground-600">
                        <i className="ri-money-dollar-circle-line text-[12px]" />
                        {formatMoney(creator.rate, creator.currency)}
                      </span>
                    </div>

                    {applicant.note ? (
                      <p className="mt-2.5 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs leading-relaxed text-foreground-600">
                        <i className="ri-double-quotes-l mr-1 text-foreground-400" />
                        {applicant.note}
                      </p>
                    ) : null}

                    {stage === 'Hired' ? (
                      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-accent-700">
                        <i className="ri-check-double-line text-[12px]" />
                        Added to the roster as an invited creator — finish the engagement in the
                        Contracts tab.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-background-100 pt-3">
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground-500">
                    <i className="ri-map-pin-time-line text-[12px]" />
                    {applicant.source} · {formatDate(applicant.appliedAt)}
                  </span>

                  <div className="flex flex-wrap items-center gap-2">
                    {stage === 'New' ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          icon="ri-search-eye-line"
                          onClick={() => onSetStage(applicant.id, 'Reviewing')}
                        >
                          Start review
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onSetStage(applicant.id, 'Declined')}
                        >
                          Decline
                        </Button>
                      </>
                    ) : null}

                    {stage === 'Reviewing' ? (
                      <>
                        <Button
                          size="sm"
                          icon="ri-user-add-line"
                          onClick={() => onHire(applicant)}
                        >
                          Invite to campaign
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onSetStage(applicant.id, 'Declined')}
                        >
                          Decline
                        </Button>
                      </>
                    ) : null}

                    {stage === 'Hired' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        icon="ri-file-paper-2-line"
                        onClick={onViewContracts}
                      >
                        Continue to contracts
                      </Button>
                    ) : null}

                    {stage === 'Declined' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon="ri-refresh-line"
                        onClick={() => onSetStage(applicant.id, 'Reviewing')}
                      >
                        Reopen
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}